// Launch Media (SM-1) — helpers for the library screen. The server decides each file's type
// (server-rest-api/mediaItems/library.js); these only label, filter and describe.

export type MediaType = "image" | "video" | "audio" | "pdf" | "doc" | "sheet" | "other";
export type MediaCampaign = { id: string; name: string; via?: "tag" | "page" };
export type MediaFile = { id: string; name: string; type: MediaType; url: string; tags: string[]; campaigns?: MediaCampaign[]; publicId?: string | null; video?: { state: "processing" | "ready" | "failed" } | null; namedLink?: NamedLink | null; addedAt: string };
// OD-3: a public file's short link on one of the account's domains
export type NamedLink = { host: string; name: string; url: string };
export type MediaCounts = Record<"all" | MediaType, number>;

// the sidebar's order
export const MEDIA_TYPES: { key: MediaType; label: string }[] = [
  { key: "image", label: "Images" },
  { key: "video", label: "Videos" },
  { key: "audio", label: "Audio" },
  { key: "pdf", label: "PDFs" },
  { key: "doc", label: "Documents" },
  { key: "sheet", label: "Spreadsheets" },
  { key: "other", label: "Other" },
];

export const visibleFiles = (files: MediaFile[], { type }: { type: MediaType | null }) => (type ? files.filter((f) => f.type === type) : files);

const DAY = 86400000;
export const addedLabel = (iso: string, now = new Date()) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())) / DAY);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

// SM-2: uploads go straight to storage. For each file: ask the server for a one-time link, send the
// file there (with progress), then confirm so the server records it. One file failing doesn't stop
// the rest. Network calls are passed in (see api/media.ts), so this is testable.
export type UploadDeps = {
  start: (f: { name: string; contentType: string; size: number }) => Promise<{ key: string; uploadUrl: string }>;
  send: (uploadUrl: string, file: File, onPct: (pct: number) => void, contentType: string) => Promise<void>;
  finish: (key: string, name: string) => Promise<MediaFile>;
};
export const uploadFiles = async (files: File[], deps: UploadDeps, onProgress: (name: string, pct: number) => void) => {
  const added: MediaFile[] = [];
  const failed: { name: string; error: string }[] = [];
  for (const file of files) {
    if (!file || file.name.startsWith(".")) continue; // .DS_Store and other hidden files
    const contentType = file.type || "application/octet-stream";
    try {
      onProgress(file.name, 0);
      const { key, uploadUrl } = await deps.start({ name: file.name, contentType, size: file.size });
      await deps.send(uploadUrl, file, (pct) => onProgress(file.name, pct), contentType);
      added.push(await deps.finish(key, file.name));
    } catch (e) {
      failed.push({ name: file.name, error: e instanceof Error ? e.message : "Couldn't upload this file." });
    }
  }
  return { added, failed };
};

// SM-5: the tag and search filters (any case; search covers names and tags). Counts per type follow
// the tag and search filters but not the type filter, so the sidebar shows where things are.
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
export const filterFiles = (files: MediaFile[], { type, tag, q, campaign = null }: { type: MediaType | null; tag: string | null; q: string; campaign?: string | null }) => {
  const needle = (q || "").trim().toLowerCase();
  const scoped = files.filter((f) =>
    (!campaign || (f.campaigns || []).some((c) => c.id === campaign)) &&
    (!tag || f.tags.some((t) => same(t, tag))) &&
    (!needle || [f.name, ...f.tags, ...(f.campaigns || []).map((c) => c.name)].join(" ").toLowerCase().includes(needle)));
  const counts = { all: 0, image: 0, video: 0, audio: 0, pdf: 0, doc: 0, sheet: 0, other: 0 } as MediaCounts;
  for (const f of scoped) { counts.all++; counts[f.type]++; }
  return { files: type ? scoped.filter((f) => f.type === type) : scoped, counts };
};

// SM-6: the campaigns that hold files, for the sidebar (names come live from the server)
export const campaignList = (files: MediaFile[]) => {
  const out = new Map<string, { id: string; name: string; count: number }>();
  for (const f of files) for (const c of f.campaigns || []) out.set(c.id, { id: c.id, name: c.name, count: (out.get(c.id)?.count || 0) + 1 });
  return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
};

// a file still uploading shows by its likely type (the server decides for good once it lands)
const EXT: Record<string, MediaType> = Object.fromEntries([
  ...["jpg", "jpeg", "png", "gif", "webp", "svg", "heic", "avif"].map((e) => [e, "image"]),
  ...["mp4", "mov", "webm", "m4v"].map((e) => [e, "video"]),
  ...["mp3", "m4a", "wav", "aac", "ogg"].map((e) => [e, "audio"]),
  ["pdf", "pdf"], ...["doc", "docx", "txt", "rtf", "md"].map((e) => [e, "doc"]), ...["csv", "xls", "xlsx", "tsv"].map((e) => [e, "sheet"]),
]);
// OD-3: a link name suggested from the file name ("Q3 Scorecard.pdf" gives "q3-scorecard")
export const suggestLinkName = (fileName: string) =>
  String(fileName || "").replace(/\.[a-z0-9]{2,4}$/i, "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 100).replace(/-+$/, "");

export const guessType = (name: string, contentType = ""): MediaType => {
  if (contentType.startsWith("image/")) return "image";
  if (contentType.startsWith("video/")) return "video";
  if (contentType.startsWith("audio/")) return "audio";
  return EXT[(name.split(".").pop() || "").toLowerCase()] || (contentType === "application/pdf" ? "pdf" : "other");
};
